import React, { useEffect, useState } from "react";
import Panel from "../../../components/ui/Panel/Panel.jsx";
import Button from "../../../components/ui/Button/Button.jsx";
import FormField from "../../../components/forms/FormField/FormField.jsx";
import TextInput from "../../../components/forms/TextInput/TextInput.jsx";
import Select from "../../../components/forms/Select/Select.jsx";
import Checkbox from "../../../components/forms/Checkbox/Checkbox.jsx";
import { US_STATES } from "../../../data/rates.js";
import { useApp } from "../../../hooks/useApp.js";
import { validatePreparer, FIELD_FORMATS_PREPARER, sanitizeEmailInput, TEXT_TYPE_STRIP } from "../../../utils/validators.js";
import { formatPhone } from "../../../utils/format.js";
import { getToken } from "../../../services/api.js";
import { getUsStates, getCountries } from "../../../services/lookupService.js";
import { getTaxPreparer, saveTaxPreparer } from "../../../services/taxPreparerService.js";

const DEFAULT_COUNTRY_OPTIONS = [{ isoCode: "US", name: "United States" }];

/* Same visual as PreparersScreen's ErrorMsg — red text + icon under field */
function ErrorMsg({ msg }) {
  if (!msg) return null;
  return (
    <p className="text-[11px] text-[#DC2626] mt-[4px] flex items-center gap-[4px]">
      <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
        <circle cx="6" cy="6" r="5.5" stroke="#DC2626" strokeWidth="1.2" />
        <path d="M6 4v3" stroke="#DC2626" strokeWidth="1.2" strokeLinecap="round" />
        <circle cx="6" cy="8.5" r="0.6" fill="#DC2626" />
      </svg>
      {msg}
    </p>
  );
}

function FormatHint({ text }) {
  if (!text) return null;
  return <p className="text-[11px] text-[var(--muted)] mt-[3px]">{text}</p>;
}

const TaxPreparerStep = ({ onNext, onDashboard, continueLabel = "Continue to business →", backLabel = "← Dashboard" }) => {
  const { state, dispatch, showToast } = useApp();
  const { preparer, preparerId } = state;
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [stateOptions, setStateOptions] = useState(US_STATES.map((s) => [s, s]));
  const [lookupsLoading, setLookupsLoading] = useState(true);
  const [countryOptions, setCountryOptions] = useState(DEFAULT_COUNTRY_OPTIONS);
  const [countriesLoading, setCountriesLoading] = useState(true);

  // errors[key] is a message STRING (or "" / undefined when valid) —
  // same shape as RULES in PreparersScreen.
  const { errors } = validatePreparer(preparer);

  const setPreparer = (patch) => dispatch({ type: "SET_PREPARER", patch });

  useEffect(() => {
    let cancelled = false;
    getUsStates()
      .then((states) => {
        if (cancelled) return;
        if (Array.isArray(states) && states.length) {
          setStateOptions(states.map((s) => [s.code, s.name]));
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLookupsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    getCountries()
      .then((list) => {
        if (!cancelled && Array.isArray(list) && list.length) {
          setCountryOptions(list);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setCountriesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // A saved foreign preparer only carries the country's name back from the API
  // (no isoCode) — resolve it against the loaded country list once both are available.
  useEffect(() => {
    if (preparer.country || !preparer.countryName || !countryOptions.length) return;
    const match = countryOptions.find((c) => c.name === preparer.countryName);
    if (match) setPreparer({ country: match.isoCode });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryOptions, preparer.countryName, preparer.country]);

  // Loads the caller's saved preparer into the form when we already know its
  // id (e.g. restored from sessionStorage on a page refresh, or set
  // explicitly by TaxPreparers.jsx's "Edit" action before navigating here).
  // Deliberately does NOT fall back to guessing "the most recent preparer"
  // when preparerId is empty - that previously clobbered the blank form
  // "Add Preparer" intentionally shows (it resets preparerId to null for
  // exactly that reason).
  useEffect(() => {
    if (!getToken() || !preparerId) return;
    let cancelled = false;

    getTaxPreparer(preparerId)
      .then((saved) => {
        if (!cancelled) setPreparer(saved);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preparerId]);

  /* ── Live input filtering ──
     - Leading space blocked instantly.
     - Trailing space allowed while typing, stripped on blur via trimTrailing(). */

  const trimTrailing = (key) => () => {
    setPreparer({ [key]: (preparer[key] || "").replace(/\s+$/, "") });
  };

  const handleNameChange = (e) => {
    const val = e.target.value
      .replace(/^ /, "")
      .replace(/[^A-Za-z0-9'\- ]/g, "")
      .replace(/ {2,}/g, " ")
      .replace(/-{2,}/g, "-")
      .replace(/'{2,}/g, "'")
      .slice(0, 35);
    setPreparer({ name: val });
  };

  const handlePtinChange = (e) => {
    const raw = e.target.value.toUpperCase().replace(/^ /, "");
    const digits = raw.replace(/[^0-9]/g, "").slice(0, 8);
    setPreparer({ ptin: raw.length ? "P" + digits : "" });
  };

  const handleEmailChange = (e) => {
    setPreparer({ email: sanitizeEmailInput(e.target.value) });
  };

  const handlePhoneChange = (e) => setPreparer({ phone: formatPhone(e.target.value) });

  const handleExtChange = (e) => setPreparer({ ext: e.target.value.replace(/\D/g, "").slice(0, 3) });

  const handleFirmNameChange = (e) => {
    const val = e.target.value
      .replace(/^ /, "")
      .replace(/[^A-Za-z0-9#()&'\- ]/g, "")
      .replace(/ {2,}/g, " ")
      .slice(0, 60);
    setPreparer({ firmName: val });
  };

  const handleFirmEinChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 9);
    setPreparer({ firmEin: digits.length > 2 ? `${digits.slice(0, 2)}-${digits.slice(2)}` : digits });
  };

  const handleAddressChange = (e) => {
    const val = e.target.value
      .replace(/^ /, "")
      .replace(/[^A-Za-z0-9\-/ ]/g, "")
      .slice(0, 35);
    setPreparer({ a1: val });
  };

  const handleAddress2Change = (e) => {
    const val = e.target.value
      .replace(/^ /, "")
      .replace(/[^A-Za-z0-9\-/ ]/g, "")
      .slice(0, 35);
    setPreparer({ a2: val });
  };

  const handleCountryChange = (e) => {
    const iso = e.target.value;
    const country = countryOptions.find((c) => c.isoCode === iso);
    setPreparer({
      country: iso,
      countryName: country?.name || "",
      kind: iso === "US" ? "us" : "foreign",
      state: "",
    });
  };

  // Non-US (Canada/Mexico): free-text Province/State - IRS TextType, max 17.
  const handleStateTextChange = (e) => {
    const val = e.target.value
      .replace(/^ /, "")
      .replace(TEXT_TYPE_STRIP, "")
      .replace(/ {2,}/g, " ")
      .slice(0, 17);
    setPreparer({ state: val });
  };

  const handleCityChange = (e) => {
    const isUs = !preparer.country || preparer.country === "US";
    const maxLen = isUs ? 22 : 50;
    const val = e.target.value
      .replace(/^ /, "")
      .replace(/[^A-Za-z ]/g, "")
      .replace(/ {2,}/g, " ")
      .slice(0, maxLen);
    setPreparer({ city: val });
  };

  const handleZipChange = (e) => {
    const isUs = !preparer.country || preparer.country === "US";
    if (isUs) {
      const digits = e.target.value.replace(/\D/g, "").slice(0, 12);
      setPreparer({ zip: digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits });
      return;
    }
    // Non-US (Canada/Mexico): free-text postal code - IRS TextType, max 16.
    const val = e.target.value
      .replace(/^ /, "")
      .replace(TEXT_TYPE_STRIP, "")
      .replace(/ {2,}/g, " ")
      .slice(0, 16);
    setPreparer({ zip: val });
  };

  const handleContinue = async () => {
    setTouched(true);
    const { ok } = validatePreparer(preparer);
    if (!ok) {
      showToast("Please fix the highlighted fields.");
      return;
    }

    if (getToken()) {
      setSaving(true);
      try {
        const saved = await saveTaxPreparer(preparerId, preparer);
        dispatch({ type: "SET_PREPARER_ID", preparerId: saved.id });
      } catch (err) {
        setSaving(false);
        showToast(err.message || "Couldn't save the tax preparer. Please try again.");
        return;
      }
      setSaving(false);
    }

    onNext();
  };

  const show = (key) => touched && !!errors[key];

  return (
    <Panel padded>
      <div className="font-disp uppercase tracking-[.14em] text-xs text-[var(--accent)] font-semibold">
         Tax preparer
      </div>
      <h2 className="text-2xl m-[4px_0]">Who's preparing this return?</h2>
      <p className="text-[var(--muted)] max-w-[62ch] mb-[18px]">
        If someone other than the business is filling this out for pay, the IRS requires their PTIN and contact
        details here. Leave this blank if you're filing it yourself.
      </p>

      <FormField label="Preparer name" required>
        <TextInput
          value={preparer.name}
          onChange={handleNameChange}
          onBlur={trimTrailing("name")}
          placeholder="Jordan Blake"
          maxLength={35}
          invalid={show("name")}
        />
        <FormatHint text={FIELD_FORMATS_PREPARER.name} />
        <ErrorMsg msg={show("name") ? errors.name : ""} />
      </FormField>

      <div className="grid gap-3.5 sm:grid-cols-2">
        <FormField label="PTIN" required>
          <TextInput
            mono
            maxLength={9}
            placeholder="P12345678"
            value={preparer.ptin}
            onChange={handlePtinChange}
            invalid={show("ptin")}
          />
          <FormatHint text={FIELD_FORMATS_PREPARER.ptin} />
          <ErrorMsg msg={show("ptin") ? errors.ptin : ""} />
        </FormField>
        {/* <FormField
          label={<>Credentials <span className="text-[var(--dim)] font-normal">· optional</span></>}
          hint={credentialsLoading ? "Loading options…" : undefined}
        >
          <Select
            value={preparer.credentials}
            onChange={(e) => setPreparer({ credentials: e.target.value })}
            disabled={credentialsLoading}
          >
            <option value="">—</option>
            {credentialOptions.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </FormField> */}
         <FormField label="Email" required>
        <TextInput
          type="email"
          placeholder="preparer@example.com"
          value={preparer.email}
          onChange={handleEmailChange}
          onBlur={() => setPreparer({ email: (preparer.email || "").trim() })}
          invalid={show("email")}
        />
        <FormatHint text={FIELD_FORMATS_PREPARER.email} />
        <ErrorMsg msg={show("email") ? errors.email : ""} />
      </FormField>
      </div>

     

      <div className="grid gap-3.5 sm:grid-cols-[1.4fr_.8fr]">
        <FormField label="Phone" required>
          <TextInput
            mono
            placeholder="(815) 555-0110"
            value={preparer.phone}
            onChange={handlePhoneChange}
            invalid={show("phone")}
          />
          <FormatHint text={FIELD_FORMATS_PREPARER.phone} />
          <ErrorMsg msg={show("phone") ? errors.phone : ""} />
        </FormField>
        <FormField label={<>Ext. <span className="text-[var(--dim)] font-normal">· optional</span></>}>
          <TextInput
            mono
            maxLength={3}
            placeholder="123"
            value={preparer.ext}
            onChange={handleExtChange}
            onBlur={trimTrailing("ext")}
            invalid={show("ext")}
          />
          <FormatHint text={FIELD_FORMATS_PREPARER.ext} />
          <ErrorMsg msg={show("ext") ? errors.ext : ""} />
        </FormField>
      </div>

      <Checkbox
        id="firmEmployee"
        title="This preparer is a firm employee"
        subtitle="Adds the firm's name and EIN below, as the IRS requires for paid preparers."
        checked={preparer.isFirmEmployee}
        onChange={(e) => setPreparer({ isFirmEmployee: e.target.checked })}
      />

      {preparer.isFirmEmployee && (
        <div className="grid gap-3.5 sm:grid-cols-2">
          <FormField label="Firm name" required>
            <TextInput
              value={preparer.firmName}
              onChange={handleFirmNameChange}
              onBlur={trimTrailing("firmName")}
              placeholder="Blake Tax Advisors LLC"
              maxLength={60}
              invalid={show("firmName")}
            />
            <FormatHint text={FIELD_FORMATS_PREPARER.firmName} />
            <ErrorMsg msg={show("firmName") ? errors.firmName : ""} />
          </FormField>
          <FormField label="Firm EIN" required>
            <TextInput
              mono
              maxLength={10}
              placeholder="12-3456789"
              value={preparer.firmEin}
              onChange={handleFirmEinChange}
              invalid={show("firmEin")}
            />
            <FormatHint text={FIELD_FORMATS_PREPARER.firmEin} />
            <ErrorMsg msg={show("firmEin") ? errors.firmEin : ""} />
          </FormField>
        </div>
      )}

      <h3 className="text-[15px] font-semibold mt-5 mb-1">Business address</h3>

      <div className="grid gap-3.5 sm:grid-cols-2">
        <FormField label="Country" required hint={countriesLoading ? "Loading options…" : undefined}>
          <Select
            value={preparer.country}
            onChange={handleCountryChange}
            invalid={show("country")}
            disabled={countriesLoading}
          >
            <option value="">—</option>
            {countryOptions.map((c) => (
              <option key={c.isoCode} value={c.isoCode}>
                {c.name}
              </option>
            ))}
          </Select>
          <ErrorMsg msg={show("country") ? errors.country : ""} />
        </FormField>

        {preparer.country === "US" ? (
          <FormField label="State" required hint={lookupsLoading ? "Loading options…" : undefined}>
            <Select
              value={preparer.state}
              onChange={(e) => setPreparer({ state: e.target.value })}
              invalid={show("state")}
              disabled={lookupsLoading}
            >
              <option value="">—</option>
              {stateOptions.map(([code, name]) => (
                <option key={code} value={code}>
                  {name}
                </option>
              ))}
            </Select>
            <ErrorMsg msg={show("state") ? errors.state : ""} />
          </FormField>
        ) : (
          <FormField
            label={preparer.country === "CA" ? "Province" : preparer.country === "MX" ? "State" : "State / Province"}
            required={!!preparer.country}
          >
            <TextInput
              placeholder={
                preparer.country === "CA" ? "e.g. Ontario" : preparer.country === "MX" ? "e.g. Jalisco" : "Select a country first"
              }
              value={preparer.state}
              onChange={handleStateTextChange}
              onBlur={trimTrailing("state")}
              maxLength={17}
              disabled={!preparer.country}
              invalid={show("state")}
            />
            <FormatHint text="Letters, numbers, and standard punctuation · Max 17 characters" />
            <ErrorMsg msg={show("state") ? errors.state : ""} />
          </FormField>
        )}
      </div>

      <FormField label="Address line 1" required>
        <TextInput
          placeholder="Street address"
          value={preparer.a1}
          onChange={handleAddressChange}
          onBlur={trimTrailing("a1")}
          maxLength={35}
          invalid={show("a1")}
        />
        <FormatHint text={FIELD_FORMATS_PREPARER.a1} />
        <ErrorMsg msg={show("a1") ? errors.a1 : ""} />
      </FormField>

      <FormField label={<>Address line 2 <span className="text-[var(--dim)] font-normal">· optional</span></>}> 
        <TextInput
          placeholder="Suite, unit, building"
          value={preparer.a2}
          onChange={handleAddress2Change}
          onBlur={trimTrailing("a2")}
          maxLength={35}
        />
        <FormatHint text={FIELD_FORMATS_PREPARER.a2} />
      </FormField>

      <div className="grid gap-3.5 sm:grid-cols-2">
        <FormField label="City" required>
          <TextInput
            placeholder="Joliet"
            value={preparer.city}
            onChange={handleCityChange}
            onBlur={trimTrailing("city")}
            maxLength={preparer.country && preparer.country !== "US" ? 50 : 22}
            invalid={show("city")}
          />
          <FormatHint
            text={
              preparer.country && preparer.country !== "US"
                ? "Letters and spaces only · Max 50 characters"
                : FIELD_FORMATS_PREPARER.city
            }
          />
          <ErrorMsg msg={show("city") ? errors.city : ""} />
        </FormField>
        <FormField label="ZIP / Postal code" required>
          <TextInput
            mono={!preparer.country || preparer.country === "US"}
            maxLength={preparer.country && preparer.country !== "US" ? 16 : 13}
            placeholder={preparer.country && preparer.country !== "US" ? "e.g. A1B 2C3" : "60432"}
            value={preparer.zip}
            onChange={handleZipChange}
            invalid={show("zip")}
          />
          <FormatHint
            text={
              preparer.country && preparer.country !== "US"
                ? "Letters, numbers, and standard punctuation · Max 16 characters"
                : FIELD_FORMATS_PREPARER.zip
            }
          />
          <ErrorMsg msg={show("zip") ? errors.zip : ""} />
        </FormField>
      </div>

      <div className="flex justify-between gap-3 mt-6">
        <Button variant="ghost" onClick={onDashboard}>
          {backLabel}
        </Button>
        <Button onClick={handleContinue} disabled={saving}>
          {saving ? "Saving…" : continueLabel}
        </Button>
      </div>
    </Panel>
  );
};

export default TaxPreparerStep;